import { useLayoutEffect } from 'react';
import type { ReactNode } from 'react';

import { deriverAccent, variablesDAccent } from '../lib/accent.js';

/**
 * Branding colors as carried by TakiConfig.branding.colors (@taki/runtime-config).
 *
 * In the Taki brand book v2 the client branding drives mainly the ACCENT; the
 * rest of the palette comes from the fixed design tokens (tokens.css). Each
 * value is a solid CSS color string: hex ("#cbfb45"), rgb(), or hsl().
 *
 * `primary` is required (it becomes --accent). `background` / `foreground` are
 * optional overrides for --bg / --ink-900; when omitted the token defaults stand.
 */
export interface ThemeColors {
  /** Drives the accent (--accent). Solid hex/rgb/hsl string. */
  primary: string;
  /** Optional override for the app background (--bg). */
  background?: string;
  /** Optional override for the default text color (--ink-900). */
  foreground?: string;
}

export interface ThemeProviderProps {
  /** Branding colors loaded at runtime from the client config. */
  colors: ThemeColors;
  children: ReactNode;
}

/**
 * Normalize a CSS color string into a solid value writable on a CSS variable.
 *
 * Tokens are solid hex (no HSL channels), so most inputs pass through unchanged.
 * We only normalize shorthand 3-digit hex to 6-digit for consistency; rgb()/hsl()
 * and full hex are handed back as-is. Unknown/empty input returns null so the
 * caller can skip the assignment and keep the token default.
 */
function toSolidColor(input: string | undefined): string | null {
  if (input === undefined) return null;
  const value = input.trim();
  if (value === '') return null;

  // Expand shorthand hex (#abc -> #aabbcc).
  const shortHex = value.match(/^#([0-9a-fA-F]{3})$/);
  if (shortHex?.[1] !== undefined) {
    const [r, g, b] = shortHex[1].split('');
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }

  // Full hex, rgb()/rgba(), hsl()/hsla(): valid solid CSS colors, pass through.
  if (
    /^#[0-9a-fA-F]{6}$/.test(value) ||
    /^rgba?\(/i.test(value) ||
    /^hsla?\(/i.test(value)
  ) {
    return value;
  }

  // Unknown format: hand it back untouched (keeps callers passing named colors etc. working).
  return value;
}

/**
 * Pose les couleurs de branding du client sur `:root`, sous les noms de tokens
 * que `@taki/ui` et le preset Tailwind consomment. A monter une seule fois pres
 * de la racine de l'app, a l'interieur de la frontiere de config.
 *
 * LE BRANDING PILOTE L'ACCENT, ET TOUTE SA FAMILLE. `--accent` seul ne suffit
 * pas : le survol, la pression, le filet, l'aplat de selection, le point,
 * l'encre et les halos de focus sont DOUZE autres tokens, accordes au lime dans
 * `tokens.css`. Les poser tous, derives de la couleur demandee
 * (`lib/accent.ts`), est la difference entre un environnement bleu et un bouton
 * bleu qui devient lime au survol.
 *
 * Deux cas, et un seul chemin de code :
 *   - `primary` est un hex : on derive et on pose les quatorze tokens. Si c'est le
 *     lime Taki, la derivation rend les valeurs de `tokens.css` telles quelles,
 *     donc l'environnement par defaut ne bouge pas d'un pixel ;
 *   - `primary` est un `rgb()` ou un `hsl()` (la config l'accepte) : la famille
 *     ne se derive pas, on pose `--accent` seul et les treize autres restent
 *     ceux du socle, exactement comme avant ce chantier.
 *
 * `--primary-foreground` ne se pose pas : c'est un alias de `--accent-label`
 * (`tokens.css`), il suit tout seul.
 *
 * `background` / `foreground` restent des remplacements facultatifs de `--bg` et
 * `--ink-900`. Le composant rend ses enfants inchanges : il ne gere que des
 * variables CSS au niveau du document, et les restaure au demontage.
 *
 * LA POSE EST UN EFFET DE DISPOSITION, PAS UN EFFET ORDINAIRE. Un `useEffect`
 * s'execute APRES la peinture : la premiere image affichee porterait le lime de
 * `tokens.css`, puis basculerait a la couleur du client. L'hote a beau garder le
 * branding en cache et le lire en synchrone, ce flash resterait. `useLayoutEffect`
 * ecrit AVANT la peinture, donc la premiere image est deja la bonne. Il n'y a pas
 * de SSR dans cette pile (`AGENTS.md` section 2), donc aucun avertissement a
 * craindre.
 */
export function ThemeProvider({ colors, children }: ThemeProviderProps) {
  useLayoutEffect(() => {
    if (typeof document === 'undefined') return;

    const root = document.documentElement;

    const assignments: Record<string, string> = {};
    const famille = deriverAccent(colors.primary);
    if (famille !== null) {
      Object.assign(assignments, variablesDAccent(famille));
    } else {
      const accent = toSolidColor(colors.primary);
      if (accent !== null) assignments['--accent'] = accent;
    }

    const background = toSolidColor(colors.background);
    if (background !== null) assignments['--bg'] = background;

    const foreground = toSolidColor(colors.foreground);
    if (foreground !== null) assignments['--ink-900'] = foreground;

    const previous: Array<[string, string]> = [];
    for (const [name, color] of Object.entries(assignments)) {
      previous.push([name, root.style.getPropertyValue(name)]);
      root.style.setProperty(name, color);
    }

    return () => {
      for (const [name, value] of previous) {
        if (value === '') {
          root.style.removeProperty(name);
        } else {
          root.style.setProperty(name, value);
        }
      }
    };
  }, [colors.primary, colors.background, colors.foreground]);

  return children;
}
